import React from 'react';
import Icon from '../../../components/AppIcon';

const InstallationStats = ({ installations }) => {
  const getStats = () => {
    const total = installations?.length;
    const planned = installations?.filter(i => i?.status === 'planned')?.length;
    const installed = installations?.filter(i => i?.status === 'installed')?.length;
    const verified = installations?.filter(i => i?.status === 'verified')?.length;
    
    const totalCost = installations?.reduce((sum, i) => sum + i?.totalCost, 0);
    const completedCost = installations?.filter(i => i?.status === 'verified')?.reduce((sum, i) => sum + i?.totalCost, 0);
    
    const completionRate = total > 0 ? Math.round((verified / total) * 100) : 0;
    
    return {
      total,
      planned,
      installed,
      verified,
      totalCost,
      completedCost,
      completionRate
    };
  };

  const stats = getStats();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const statCards = [
    {
      title: 'Total Installations',
      value: stats?.total,
      icon: 'Package',
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200'
    },
    {
      title: 'Planned',
      value: stats?.planned,
      icon: 'Calendar',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      title: 'Installed',
      value: stats?.installed,
      icon: 'CheckCircle',
      color: 'text-[#7BA428]',
      bgColor: 'bg-[#A3D977]/20',
      borderColor: 'border-[#A3D977]'
    },
    {
      title: 'Verified',
      value: stats?.verified,
      icon: 'Shield',
      color: 'text-[#28A745]',
      bgColor: 'bg-[#28A745]/20',
      borderColor: 'border-[#28A745]'
    },
    {
      title: 'Completion Rate',
      value: `${stats?.completionRate}%`,
      icon: 'TrendingUp',
      color: 'text-primary',
      bgColor: 'bg-primary/5',
      borderColor: 'border-primary/20'
    }
  ];

  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
        {statCards?.map((stat, index) => (
          <div
            key={index}
            className={`bg-card border ${stat?.borderColor} rounded-lg p-4 hover:shadow-soft transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stat?.title}</p>
                <p className="text-2xl font-bold text-foreground mt-1">{stat?.value}</p>
              </div>
              <div className={`w-10 h-10 ${stat?.bgColor} rounded-lg flex items-center justify-center`}>
                <Icon name={stat?.icon} size={20} className={stat?.color} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="DollarSign" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">Total Investment</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Budget:</span>
              <span className="font-medium text-foreground">{formatCurrency(stats?.totalCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Completed Value:</span>
              <span className="font-medium text-[#28A745]">{formatCurrency(stats?.completedCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pending Value:</span>
              <span className="font-medium text-[#7BA428]">{formatCurrency(stats?.totalCost - stats?.completedCost)}</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <h3 className="font-semibold text-foreground">Status Overview</h3>
          </div>
          <div className="space-y-2">
            {stats?.planned > 0 && (
              <div className="flex items-center justify-between text-blue-600">
                <span>Planned installations:</span>
                <span className="font-medium">{stats?.planned}</span>
              </div>
            )}
            {stats?.installed > 0 && (
              <div className="flex items-center justify-between text-[#7BA428]">
                <span>Awaiting verification:</span>
                <span className="font-medium">{stats?.installed}</span>
              </div>
            )}
            {stats?.verified > 0 && (
              <div className="flex items-center justify-between text-[#28A745]">
                <span>Completed & verified:</span>
                <span className="font-medium">{stats?.verified}</span>
              </div>
            )}
            {stats?.planned === 0 && stats?.installed === 0 && stats?.verified > 0 && (
              <p className="text-[#28A745]">All installations completed!</p>
            )}
            {stats?.total === 0 && (
              <p className="text-muted-foreground">No installations yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationStats;